import { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import PageHeader from "@/components/PageHeader";
import { fmtKg, fmtNum } from "@/lib/format";

const SOURCES = ["Materials", "Equipment", "Fuel", "Electricity", "Water", "Waste", "Logistics", "Workforce Travel"];

const FACTORS = {
  Electricity: { unit: "kWh", factor: 0.207 },
  Fuel: { unit: "L", factor: 2.546 },
  Materials: { unit: "t", factor: 0.5 },
  Equipment: { unit: "h", factor: 18.0 },
  Water: { unit: "m3", factor: 0.344 },
  Waste: { unit: "t", factor: 21.0 },
  Logistics: { unit: "t-km", factor: 0.105 },
  "Workforce Travel": { unit: "km", factor: 0.171 },
};

export default function Monthly() {
  const { active } = useProjects();
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState({
    month: new Date().toISOString().slice(0, 7),
    source: "Electricity",
    amount: 1000,
    unit: "kWh",
    factor: 0.207,
    notes: "",
  });

  const load = () => {
    if (!active) return;
    api.get(`/projects/${active.id}/monthly`).then((r) => setEntries(r.data.items));
  };

  useEffect(() => {
    load();
  }, [active]);

  const onSourceChange = (s) => {
    const f = FACTORS[s];
    setForm({ ...form, source: s, unit: f.unit, factor: f.factor });
  };

  const addEntry = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/projects/${active.id}/monthly`, form);
      setEntries((arr) => [...arr, data].sort((a, b) => a.month.localeCompare(b.month)));
      toast.success("Entry added");
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  const deleteEntry = async (id) => {
    await api.delete(`/projects/${active.id}/monthly/${id}`);
    setEntries(entries.filter((e) => e.id !== id));
  };

  if (!active) return <div className="p-12 theme-ink-muted">Select a project first.</div>;

  // chart data
  const byMonth = {};
  entries.forEach((e) => {
    byMonth[e.month] = byMonth[e.month] || { month: e.month };
    byMonth[e.month][e.source] = (byMonth[e.month][e.source] || 0) + e.emissions_kg / 1000;
  });
  const chartData = Object.values(byMonth).sort((a, b) => a.month.localeCompare(b.month));
  const totalKg = entries.reduce((s, e) => s + e.emissions_kg, 0);

  const COLORS = { Electricity: "#D4AF37", Fuel: "#F87171", Materials: "#38BDF8", Logistics: "#818CF8", Waste: "#FBBF24", Water: "#4ADE80", Equipment: "#A78BFA", "Workforce Travel": "#34D399" };

  return (
    <div className="animate-fade-up">
      <PageHeader
        overline="Module · Operational"
        title="Monthly Carbon Tracking"
        subtitle="Log monthly emissions across sources to build operational carbon and feed the forecasting engine."
        kicker={`TOTAL TRACKED · ${fmtKg(totalKg)}`}
        guideId="monthly"
      />

      <section className="px-8 pt-8 grid grid-cols-1 xl:grid-cols-3 gap-4">
        <form onSubmit={addEntry} className="border border theme-border theme-surface p-6 space-y-3" data-testid="monthly-form">
          <div className="overline">New Entry</div>
          <h3 className="font-serif-display text-xl">Log emissions</h3>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1.5">Month</label>
              <input
                type="month"
                value={form.month}
                onChange={(e) => setForm({ ...form, month: e.target.value })}
                className="w-full theme-input border border theme-border px-3 py-2 text-sm rounded-sm font-mono-data focus:outline-none focus:border-gold"
              />
            </div>
            <div>
              <label className="overline block mb-1.5">Source</label>
              <select
                value={form.source}
                onChange={(e) => onSourceChange(e.target.value)}
                className="w-full theme-input border border theme-border px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-gold"
              >
                {SOURCES.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1.5">Amount</label>
              <input
                type="number"
                step="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })}
                className="w-full theme-input border border theme-border px-3 py-2 text-sm rounded-sm font-mono-data focus:outline-none focus:border-gold"
              />
            </div>
            <div>
              <label className="overline block mb-1.5">Unit</label>
              <input
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                className="w-full theme-input border border theme-border px-3 py-2 text-sm rounded-sm font-mono-data focus:outline-none focus:border-gold"
              />
            </div>
          </div>

          <div>
            <label className="overline block mb-1.5">Factor (kgCO₂e per unit)</label>
            <input
              type="number"
              step="0.001"
              value={form.factor}
              onChange={(e) => setForm({ ...form, factor: Number(e.target.value) })}
              className="w-full theme-input border border theme-border px-3 py-2 text-sm rounded-sm font-mono-data focus:outline-none focus:border-gold"
            />
          </div>

          <div className="text-xs theme-ink-muted font-mono-data">
            Computed: {fmtNum(form.amount * form.factor)} kgCO₂e
          </div>

          <button
            type="submit"
            data-testid="monthly-add-btn"
            className="w-full bg-gold text-black font-medium py-2.5 rounded-sm hover:bg-[#e0bc40] flex items-center justify-center gap-2"
          >
            <Plus size={16} weight="bold" /> Add entry
          </button>
        </form>

        <div className="xl:col-span-2 border border theme-border theme-surface p-6">
          <div className="overline">Stacked monthly emissions</div>
          <div className="font-serif-display text-2xl">By source · tCO₂e</div>
          <div className="h-72 mt-3">
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="month" stroke="#71717A" />
                <YAxis stroke="#71717A" />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {SOURCES.map((s) => (
                  <Bar key={s} dataKey={s} stackId="m" fill={COLORS[s] || "#888"} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      <section className="px-8 pt-6 pb-12">
        <div className="border border theme-border theme-surface overflow-hidden">
          <div className="px-6 py-4 border-b theme-border overline">Entries · {entries.length}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black/[0.02] dark:bg-white/[0.02] text-[10px] uppercase tracking-[0.2em] theme-ink-dim">
                <tr>
                  <th className="text-left px-6 py-3">Month</th>
                  <th className="text-left px-3 py-3">Source</th>
                  <th className="text-right px-3 py-3">Amount</th>
                  <th className="text-left px-3 py-3">Unit</th>
                  <th className="text-right px-3 py-3">Factor</th>
                  <th className="text-right px-6 py-3">Emissions</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} className="border-t border-white/5 hover:bg-black/[0.02] dark:bg-white/[0.02]">
                    <td className="px-6 py-3 font-mono-data">{e.month}</td>
                    <td className="px-3 theme-ink-muted">{e.source}</td>
                    <td className="px-3 py-3 text-right font-mono-data">{fmtNum(e.amount, 2)}</td>
                    <td className="px-3 theme-ink-muted">{e.unit}</td>
                    <td className="px-3 py-3 text-right font-mono-data theme-ink-muted">{fmtNum(e.factor, 3)}</td>
                    <td className="px-6 py-3 text-right font-mono-data text-gold">{fmtKg(e.emissions_kg)}</td>
                    <td className="px-3 py-3 text-right">
                      <button onClick={() => deleteEntry(e.id)} className="theme-ink-dim hover:text-risk">
                        <Trash size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
                {entries.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center theme-ink-dim">No entries yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}
